import React, { useState, useRef, useEffect } from 'react';
import './RegionSelector.css';

interface Region {
  id: string;
  name: string;
  manager: string;
  username: string; // Regional manager username for API queries
  district_count: number;
  dealer_count: number;
}

interface RegionSelectorProps {
  regions: Region[];
  selectedRegion: Region | null;
  onChange: (region: Region | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

const RegionSelector: React.FC<RegionSelectorProps> = ({
  regions,
  selectedRegion,
  onChange,
  placeholder = 'Select a region',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter regions based on search term
  const filteredRegions = regions.filter(region =>
    region.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    region.manager.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (region: Region | null) => {
    onChange(region);
    setIsOpen(false);
    setSearchTerm('');
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
      }
    }
  };

  return (
    <div className="region-selector" ref={dropdownRef}>
      <div 
        className={`region-selector-trigger ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
        onClick={toggleDropdown}
      >
        <span className="selected-value">
          {selectedRegion ? selectedRegion.name : placeholder}
        </span>
        <svg className="dropdown-arrow" width="12" height="8" viewBox="0 0 12 8" fill="none">
          <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      {isOpen && (
        <div className="region-selector-dropdown">
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search regions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="region-list">
            <div
              className={`region-option ${!selectedRegion ? 'selected' : ''}`}
              onClick={() => handleSelect(null)}
            >
              <span className="region-name">All Regions</span>
              <span className="region-info">View all regions</span>
            </div>

            {filteredRegions.map(region => (
              <div
                key={region.id}
                className={`region-option ${selectedRegion?.id === region.id ? 'selected' : ''}`}
                onClick={() => handleSelect(region)}
              >
                <span className="region-name">{region.name}</span>
                <span className="region-info">
                  {region.district_count} districts, {region.dealer_count} dealers
                </span>
              </div>
            ))}

            {filteredRegions.length === 0 && (
              <div className="no-results">
                No regions found matching "{searchTerm}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RegionSelector;
